import { useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";

// Preview diagnostic (only rendered when an audio test mode is armed):
// requested getUserMedia audio constraints vs. what the browser reports via
// MediaStreamTrack.getSettings(). Missing keys are shown as "unreported".
const KEYS = [["autoGainControl", "AGC"], ["echoCancellation", "EC"], ["noiseSuppression", "NS"]];

const fmt = (v) => (v === undefined || v === null ? "unreported" : v === true ? "on" : v === false ? "off" : String(v));

export default function MicSettingsReadout({ requested }) {
  const room = useRoomContext();
  const [actual, setActual] = useState(null);

  useEffect(() => {
    if (!room) return undefined;
    const read = () => {
      const pub = room.localParticipant?.getTrackPublication?.(Track.Source.Microphone);
      const mst = pub?.track?.mediaStreamTrack;
      if (!mst) { setActual(null); return; }
      try { setActual({ ...mst.getSettings(), _label: mst.label, _muted: !!pub.isMuted }); } catch { setActual({}); }
    };
    read();
    const evs = [RoomEvent.LocalTrackPublished, RoomEvent.LocalTrackUnpublished, RoomEvent.TrackMuted, RoomEvent.TrackUnmuted, RoomEvent.Connected];
    evs.forEach((e) => room.on(e, read));
    const t = setInterval(read, 5000);
    return () => { evs.forEach((e) => room.off(e, read)); clearInterval(t); };
  }, [room]);

  const leveler = "off (F2: inert leveler skipped)";

  return (
    <div data-testid="mic-settings-readout" className="absolute top-20 left-1/2 -translate-x-1/2 z-[60] rounded-md bg-black/80 text-white text-[10px] font-mono px-2 py-1 leading-tight">
      <div className="text-amber-300">mic settings — requested → actual</div>
      {actual ? (
        <>
          {KEYS.map(([k, label]) => (
            <div key={k} data-testid={`mic-setting-${k}`}>
              {label}: {fmt(requested?.[k])} → <span className={actual[k] === undefined ? "text-zinc-400" : requested?.[k] === actual[k] ? "text-emerald-300" : "text-rose-300"}>{fmt(actual[k])}</span>
            </div>
          ))}
          <div data-testid="mic-setting-rate">rate {fmt(actual.sampleRate)} · ch {fmt(actual.channelCount)} · {actual._muted ? "muted" : "live"}</div>
          <div className="text-zinc-400 truncate max-w-[220px]">{actual._label || "device unreported"}</div>
        </>
      ) : (
        <div data-testid="mic-setting-none">no local mic track (listener)</div>
      )}
      <div data-testid="mic-setting-leveler">listener leveler: {leveler}</div>
    </div>
  );
}
